import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { getPosition, API, inr } from "@/api";
import { Camera, CheckCircle2, MapPin, ArrowLeft, KeyRound } from "lucide-react";

const STEPS = [
  { type: "arrived", label: "Arrived at Site", hint: "Take a site photo at the customer location" },
  { type: "job_completed", label: "Job Completed", hint: "Take an after-work photo" },
];

function CameraCapture({ onCapture, testid, label }) {
  const ref = useRef();
  const handle = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => onCapture(reader.result);
    reader.readAsDataURL(file);
  };
  return (
    <>
      <input ref={ref} type="file" accept="image/*" capture="environment" className="hidden" data-testid={`${testid}-file`} onChange={handle} />
      <button type="button" data-testid={testid} onClick={() => ref.current.click()}
        className="w-full py-3.5 rounded-xl border-2 border-dashed border-border text-muted-foreground flex items-center justify-center gap-2 text-sm font-medium active:bg-surface-elevated">
        <Camera size={18} /> {label}
      </button>
    </>
  );
}

export default function EmpJob() {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [stock, setStock] = useState([]);
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const [exp, setExp] = useState({ amount: "", category: "fare", note: "", photo_data: null });
  const [consum, setConsum] = useState({ item_id: "", qty: "" });
  const [otpCode, setOtpCode] = useState("");
  const [otpSent, setOtpSent] = useState(false);

  const load = useCallback(() => {
    api.get(`/jobs/${id}`).then((r) => setJob(r.data)).catch(() => toast.error("Job not found"));
    api.get("/stock").then((r) => setStock(r.data));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!job) return <div className="p-5 text-muted-foreground">Loading…</div>;
  const doneTypes = new Set(job.checkpoints.map((c) => c.type));
  const nextStep = STEPS.find((s) => !doneTypes.has(s.type));

  const doCheckpoint = async (type) => {
    if (!photo) return toast.error("Photo is required");
    setBusy(true);
    const pos = await getPosition();
    try {
      const r = await api.post(`/jobs/${id}/checkpoints`, { type, lat: pos?.lat, lng: pos?.lng, photo_data: photo });
      setPhoto(null);
      if (r.data.distance_m != null && !r.data.geofence_pass) toast.warning(`Saved, but you are ${r.data.distance_m}m from the site (flagged)`);
      else toast.success("Checkpoint saved ✓");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const addExpense = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/jobs/${id}/expenses`, { ...exp, amount: parseFloat(exp.amount) });
      toast.success("Expense saved"); setExp({ amount: "", category: "fare", note: "", photo_data: null }); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const addConsumable = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/jobs/${id}/consumables`, { item_id: consum.item_id, qty: parseFloat(consum.qty) });
      toast.success("Usage logged"); setConsum({ item_id: "", qty: "" }); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const sendOtp = async () => {
    try {
      const r = await api.post(`/jobs/${id}/otp`);
      setOtpSent(true);
      toast.success(`OTP sent to customer (demo code: ${r.data.dev_code})`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const verifyOtp = async () => {
    try {
      await api.post(`/jobs/${id}/otp/verify`, { code: otpCode });
      toast.success("Customer OTP verified — job confirmed!");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Wrong OTP"); }
  };

  const back = `${API.replace("/api", "")}/api`;

  return (
    <div className="p-5 space-y-5" data-testid="emp-job-page">
      <div className="flex items-center gap-3">
        <Link to="/app" data-testid="emp-job-back" className="w-10 h-10 rounded-full bg-surface border border-border flex items-center justify-center"><ArrowLeft size={18} /></Link>
        <div>
          <div className="font-mono text-xs text-muted-foreground">{job.job_no}</div>
          <h1 className="font-heading font-bold leading-tight">{job.service}</h1>
        </div>
      </div>

      <div className="bg-surface border border-border rounded-2xl p-4 text-sm space-y-1.5">
        <div className="font-semibold">{job.customer_name} · <a href={`tel:${job.phone}`} className="text-primary">{job.phone}</a></div>
        <div className="flex items-start gap-1.5 text-muted-foreground"><MapPin size={14} className="mt-0.5 shrink-0" /> {job.address || "Address on file"}</div>
        <div className="text-muted-foreground text-xs">Value: <span className="font-mono text-muted-foreground">{inr(job.amount)}</span> · Status: <span className="text-muted-foreground">{job.status}</span>{job.otp_verified && " · OTP ✓"}</div>
      </div>

      <div className="space-y-2">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Checkpoints</div>
        {job.checkpoints.map((c) => (
          <div key={c.id} className="flex items-center gap-3 bg-surface border border-border rounded-xl p-3">
            <CheckCircle2 size={20} className={c.geofence_pass ? "text-success" : "text-warning"} />
            <div className="flex-1 text-sm">
              <div className="font-medium">{{ start_duty: "Start Duty", arrived: "Arrived at Site", job_completed: "Job Completed", end_duty: "End Duty" }[c.type]}</div>
              <div className="text-xs text-muted-foreground">{new Date(c.created_at).toLocaleString("en-IN")}{c.distance_m != null && ` · ${c.distance_m}m ${c.geofence_pass ? "✓" : "⚠ flagged"}`}</div>
            </div>
            {c.photo_url && <img src={`${back}${c.photo_url.replace("/api", "")}`} alt="" className="w-11 h-11 rounded-lg object-cover border border-border" onError={(e) => { e.target.style.display = "none"; }} />}
          </div>
        ))}
      </div>

      {nextStep && ["Assigned", "In Progress"].includes(job.status) && (
        <div className="bg-surface border border-primary/30 rounded-2xl p-4 space-y-3" data-testid="checkpoint-card">
          <div className="font-heading font-bold text-primary">Next: {nextStep.label}</div>
          <div className="text-xs text-muted-foreground">{nextStep.hint} — GPS will be captured automatically.</div>
          {photo && <img src={photo} alt="captured" className="w-full h-40 object-cover rounded-xl border border-border" data-testid="checkpoint-photo-preview" />}
          <CameraCapture testid={`capture-${nextStep.type}`} label={photo ? "Retake Photo" : "Take Photo"} onCapture={setPhoto} />
          <button data-testid={`submit-${nextStep.type}`} onClick={() => doCheckpoint(nextStep.type)} disabled={busy || !photo}
            className="w-full py-4 rounded-xl bg-primary text-primary-foreground font-heading font-extrabold disabled:opacity-40">
            {busy ? "Capturing GPS…" : `CONFIRM — ${nextStep.label.toUpperCase()}`}
          </button>
        </div>
      )}

      {doneTypes.has("job_completed") && !job.otp_verified && (
        <div className="bg-surface border border-warning rounded-2xl p-4 space-y-3" data-testid="otp-card">
          <div className="font-heading font-bold text-warning-foreground flex items-center gap-2"><KeyRound size={16} /> Customer Confirmation</div>
          {!otpSent ? (
            <button data-testid="send-otp-button" onClick={sendOtp} className="w-full py-3.5 rounded-xl bg-warning text-foreground font-bold">Send OTP to Customer</button>
          ) : (
            <div className="flex gap-2">
              <input data-testid="otp-input" value={otpCode} onChange={(e) => setOtpCode(e.target.value)} placeholder="6-digit OTP" maxLength={6}
                className="flex-1 bg-background border border-border rounded-xl px-4 py-3 font-mono text-lg tracking-widest text-center" />
              <button data-testid="verify-otp-button" onClick={verifyOtp} className="px-5 rounded-xl bg-primary text-primary-foreground font-bold">Verify</button>
            </div>
          )}
        </div>
      )}

      {["Assigned", "In Progress"].includes(job.status) && (
        <>
          <form onSubmit={addExpense} className="bg-surface border border-border rounded-2xl p-4 space-y-3" data-testid="expense-form">
            <div className="font-heading font-bold">Add Expense</div>
            <div className="flex gap-2">
              <input data-testid="expense-amount-input" type="number" min="1" step="0.01" placeholder="₹ Amount" value={exp.amount} onChange={(e) => setExp({ ...exp, amount: e.target.value })} required
                className="flex-1 bg-background border border-border rounded-xl px-4 py-3" />
              <select data-testid="expense-category-select" value={exp.category} onChange={(e) => setExp({ ...exp, category: e.target.value })}
                className="bg-background border border-border rounded-xl px-3">
                <option value="fare">Fare</option><option value="misc">Misc</option>
              </select>
            </div>
            <input data-testid="expense-note-input" placeholder="Note (optional)" value={exp.note} onChange={(e) => setExp({ ...exp, note: e.target.value })}
              className="w-full bg-background border border-border rounded-xl px-4 py-3" />
            <CameraCapture testid="capture-receipt" label={exp.photo_data ? "Receipt attached ✓" : "Receipt photo (optional)"} onCapture={(d) => setExp({ ...exp, photo_data: d })} />
            <button data-testid="expense-save-button" type="submit" className="w-full py-3.5 rounded-xl bg-surface-elevated font-bold border border-border">Save Expense</button>
          </form>

          <form onSubmit={addConsumable} className="bg-surface border border-border rounded-2xl p-4 space-y-3" data-testid="consumable-form">
            <div className="font-heading font-bold">Log Material Used</div>
            <select data-testid="consumable-item-select" value={consum.item_id} onChange={(e) => setConsum({ ...consum, item_id: e.target.value })} required
              className="w-full bg-background border border-border rounded-xl px-4 py-3">
              <option value="">Select item…</option>
              {stock.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.stock_level} {s.unit} left)</option>)}
            </select>
            <input data-testid="consumable-qty-input" type="number" min="0.1" step="0.1" placeholder="Quantity used" value={consum.qty} onChange={(e) => setConsum({ ...consum, qty: e.target.value })} required
              className="w-full bg-background border border-border rounded-xl px-4 py-3" />
            <button data-testid="consumable-save-button" type="submit" className="w-full py-3.5 rounded-xl bg-surface-elevated font-bold border border-border">Log Usage</button>
          </form>
        </>
      )}
    </div>
  );
}
